/**
 * System which processes and holds logs for debug.
 * @module LogBuffer
 * @class LogBuffer
 * @param {Phaser.Game} game - game to which this buffer belongs
 */

const LogBuffer = class {
    constructor ( game ) {

        /**
         * Record of recent logs, oldest to youngest
         */
        this.buffer = [];

        /**
         * Number of records to keep at once
         * @default 10
         * @constant
         */
        this.BUFFER_MAX = 10;

        /**
         * Whether this should display a debug overlay on the game.
         * Used to coordinate an external call to create the overlay,
         * which cannot be booted at game init.
         */
        this.debug = false;

        /**
         * Game object displaying frame rate, scene population etc.
         * in the overlay. This should be positioned in the top left corner
         * of the screen.
         * @type {Phaser.GameObjects.Text}
         * @default null
         */
        this.diagnosticText = null;

        /**
         * Game to which this buffer belongs
         */
        this.game = game;

        /**
         * Overlay scene to which logs may be rendered.
         * @type {Phaser.Scene}
         * @default null
         */
        this.overlay = null;

        /**
         * Game object displaying logs in the overlay.
         * This should always have its top right corner
         * aligned to the top right corner of the screen.
         * @type {Phaser.GameObjects.Text}
         * @default null
         */
        this.text = null;
    }

    /**
     * Add a debug overlay scene to the game.
     * This will display the log buffer, overlaid onto the game
     * in its own scene.
     */
    addDebug () {
        if ( this.overlay ) {
            if ( !this.overlay.scene.isActive() ) {
                this.overlay.scene.start();
            }
            return;
        }
        this.overlay = this.game.scene.add( "log-buffer-overlay", {} );
        this.overlay.events.on( "ready", this.addText, this );
        this.overlay.scene.start();
    }

    /**
     * Set up the text objects and background.
     */
    addText () {
        const width = this.game.config.width;
        const height = this.game.config.height;
        this.background = this.overlay.add.rectangle(
            width / 2, height / 2, width, height, 0xcc1111, 0.3 );

        this.text = this.overlay.add.text( 0, 20, "", { align: "right" } );
        this.renderText();

        this.diagnosticText =
            this.overlay.add.text( 10, 20, "", { align: "left" } );

        // Force diagnostic text to update regularly.
        // NOTE: We subtract 1 from child population,
        // because there's a debug canvas visible on standard levels.
        this.overlay.update = ( time, delta ) => {
            let children = [];
            const current = this.game.levelManager.getCurrent();
            if ( current ) {
                children = current.getChildren();
            }
            const out =
                "Level Members: " + Math.max( children.length - 1, 0 ) + "\n" +
                "Frames per Second: " + ( 1000 / delta ).toFixed( 0 );

            // Don't worry about redrawing text; Phaser will only redraw
            // if the string has changed.
            this.diagnosticText.setText( out );
        };
    }

    /**
     * Log a message to the console.
     * Make the message available in the buffer for future debug.
     * @param {String} message - message to log
     * @param {String} [tag="#Script"] - tag describing message category
     */
    log ( message, tag ) {
        if ( tag ) {
            console.log( tag, message );
        } else {
            console.log( "#Script", message );
        }
        this.buffer.push( message );
        while ( this.buffer.length > this.BUFFER_MAX ) {
            // Remove the first, oldest, element in the buffer.
            this.buffer.shift();
        }

        if ( this.overlay && this.overlay.scene.isActive() ) {
            this.renderText();
        }
    }

    /**
     * Hide the debug overlay scene, if it is visible.
     */
    removeDebug () {
        if ( !this.overlay || !this.overlay.scene.isActive() ) {
            return;
        }
        this.overlay.scene.stop();
    }

    /**
     * Update the debug text display with the latest buffer contents.
     */
    renderText () {
        const str = this.buffer.join( "\n" );
        this.text.setText( str );

        // Align to top right corner.
        this.text.x =
            this.game.canvas.width - this.text.getBounds().width - 10;
    }
};

export default LogBuffer;
